/**
 * PFH mobile API — Capacitor-aware, replaces @foroba/ui/api for pfh-mobile.
 *
 * This module is aliased over @foroba/ui/api in vite.config.ts so that ALL
 * callers (including those that re-export from @foroba/ui/api) get the
 * Capacitor-aware storage instead of the pre-compiled localStorage version.
 *
 * Storage (#30, M5):
 *   - iOS native: Keychain via @aparajita/capacitor-secure-storage, accessibility
 *     kSecAttrAccessibleWhenUnlockedThisDeviceOnly, iCloud sync off (never in backups).
 *     Legacy @capacitor/preferences values are migrated once, then wiped.
 *   - Web/PWA:    no tokens at all (Plan AU M-8) — Foroba's httponly `suite_session`
 *     cookie carries auth; tokens left by older builds are purged once at startup.
 *   - Memory cache keeps the getters synchronous; initStorage() loads the
 *     cache once at app startup before first render.
 *
 * Server URL (#49):
 *   - VITE_API_BASE_URL (empty = dev/PWA relative paths)
 *   - Always uses versioned /api/v1/ prefix (ADR 0001) so dev proxy and
 *     production nginx use the same path shape.
 */

import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { apiErrorMessage } from './apiError'
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage'
import { migrateKeys, type KV } from './storageMigration'
import { classifyTokenResponse } from '../auth/tokenResponse'
import { clearStepupTokens, setStepupToken, type StepupApp } from '../auth/stepupStore'
import { authRoutes, classifyCookieLogin, purgeLegacyTokens } from './webSession'

// ── Storage ──────────────────────────────────────────────────────────────────

const ACCESS_KEY = 'pfh_access_token'
const REFRESH_KEY = 'pfh_refresh_token'
const BIOMETRIC_KEY = 'pfh_biometric_enabled'

const isNative = Capacitor.isNativePlatform()

const secureKV: KV = {
  get: async (k) => {
    const v = await SecureStorage.get(k, false, false)
    return typeof v === 'string' ? v : null
  },
  set: (k, v) => SecureStorage.set(k, v, false, false, KeychainAccess.whenUnlockedThisDeviceOnly),
  remove: async (k) => { await SecureStorage.remove(k, false) },
}

const legacyKV: KV = {
  get: async (k) => (await Preferences.get({ key: k })).value,
  set: (k, v) => Preferences.set({ key: k, value: v }),
  remove: (k) => Preferences.remove({ key: k }),
}

// In-memory cache — loaded once by initStorage(), then kept in sync.
let _access: string | null = null
let _refresh: string | null = null
let _biometricEnabled = false

/**
 * Must be called once before first render. Loads tokens from Keychain (iOS)
 * into the in-memory cache; on web only the biometric flag is read.
 */
export async function initStorage(): Promise<void> {
  if (isNative) {
    await SecureStorage.setSynchronize(false)
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly)
    await migrateKeys(legacyKV, secureKV, [ACCESS_KEY, REFRESH_KEY, BIOMETRIC_KEY])
    const [a, r, b] = await Promise.all([
      secureKV.get(ACCESS_KEY), secureKV.get(REFRESH_KEY), secureKV.get(BIOMETRIC_KEY),
    ])
    _access = a
    _refresh = r
    _biometricEnabled = b === 'true'
  } else {
    purgeLegacyTokens(localStorage)
    _biometricEnabled = localStorage.getItem(BIOMETRIC_KEY) === 'true'
  }
}

export function getBiometricEnabled(): boolean {
  return _biometricEnabled
}

export function setBiometricEnabled(enabled: boolean): void {
  _biometricEnabled = enabled
  const val = enabled ? 'true' : 'false'
  if (isNative) {
    void secureKV.set(BIOMETRIC_KEY, val)
  } else {
    localStorage.setItem(BIOMETRIC_KEY, val)
  }
}

export function getAccessToken(): string | null {
  return _access
}

export function getRefreshToken(): string | null {
  return _refresh
}

export function setTokens(access: string, refresh: string): void {
  _access = access
  _refresh = refresh
  if (isNative) {
    // Fire-and-forget — callers are synchronous
    void secureKV.set(ACCESS_KEY, access)
    void secureKV.set(REFRESH_KEY, refresh)
  }
}

export function clearTokens(): void {
  clearStepupTokens()
  _access = null
  _refresh = null
  if (isNative) {
    void secureKV.remove(ACCESS_KEY)
    void secureKV.remove(REFRESH_KEY)
  }
}

// ── API base URL (#49) ────────────────────────────────────────────────────────
//
// Dev/PWA:  VITE_API_BASE_URL is empty → relative paths → Vite proxy / nginx
// Native:   VITE_API_BASE_URL=https://myserver.example.com → absolute paths
//
// Always use /api/v1/ prefix so the same path works in dev proxy and nginx.

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '') + '/api/v1/foroba'

const DEVICE_LABEL = 'PFH Mobile'
const ROUTES = authRoutes(isNative)

// ── API types ─────────────────────────────────────────────────────────────────

export interface SessionUser {
  id: number
  email: string
  display_name: string | null
  role: 'base' | 'expert'
  tier: 'free' | 'premium'
  is_admin: boolean
  mfa_enabled: boolean
  preferred_currency: string
}

// ── API helpers ───────────────────────────────────────────────────────────────

/** Fetch with Authorization: Bearer, auto-refreshing on 401. */
export async function apiFetch(path: string, init?: RequestInit, retry = true): Promise<Response> {
  const token = getAccessToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (isNative) headers['X-Client'] = 'native'

  const r = await fetch(`${API_BASE}${path}`, { credentials: ROUTES.credentials, ...init, headers })

  if (r.status === 401 && retry) {
    const refreshed = await tryRefresh()
    if (refreshed) return apiFetch(path, init, false)
  }
  return r
}

export async function tryRefresh(): Promise<boolean> {
  const rt = getRefreshToken()
  if (!rt) return false
  try {
    const r = await fetch(`${API_BASE}/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!r.ok) { clearTokens(); return false }
    const body = await r.json() as { access_token: string; refresh_token: string }
    setTokens(body.access_token, body.refresh_token)
    return true
  } catch {
    clearTokens()
    return false
  }
}

export async function fetchMe(): Promise<SessionUser | null> {
  // Web: no token to check — the httponly cookie (if any) decides.
  if (isNative && !getAccessToken()) return null
  const r = await apiFetch('/auth/me')
  if (r.status === 401) { clearTokens(); return null }
  if (!r.ok) throw new Error(`/auth/me ${r.status}`)
  return r.json() as Promise<SessionUser>
}

export async function login(
  email: string,
  password: string,
): Promise<{ ok: boolean; mfa_required?: boolean; methods?: string[] }> {
  if (!isNative) {
    const r = await fetch(`${API_BASE}${ROUTES.login}`, {
      method: 'POST',
      credentials: ROUTES.credentials,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const out = classifyCookieLogin(r.status, await r.json().catch(() => ({})))
    if (out.kind === 'session') return { ok: true }
    if (out.kind === 'mfa') return { ok: false, mfa_required: true, methods: out.methods }
    throw new Error(out.message)
  }
  const r = await fetch(`${API_BASE}${ROUTES.login}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(isNative ? { 'X-Client': 'native' } : {}),
    },
    body: JSON.stringify({ email, password, device_label: DEVICE_LABEL }),
  })
  const out = classifyTokenResponse(r.status, await r.json().catch(() => ({})))
  if (out.kind === 'tokens') {
    setTokens(out.access_token, out.refresh_token)
    return { ok: true }
  }
  if (out.kind === 'mfa') return { ok: false, mfa_required: true, methods: out.methods }
  throw new Error(out.message)
}

export async function loginWithMfa(email: string, code: string, method: string): Promise<void> {
  const r = await fetch(`${API_BASE}${ROUTES.mfa}`, {
    method: 'POST',
    credentials: ROUTES.credentials,
    headers: {
      'Content-Type': 'application/json',
      ...(isNative ? { 'X-Client': 'native' } : {}),
    },
    body: JSON.stringify(isNative ? { email, code, method, device_label: DEVICE_LABEL } : { email, code, method }),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(apiErrorMessage(body, `MFA failed (${r.status})`))
  }
  if (!isNative) return // cookie set by the response
  const body = await r.json() as { access_token: string; refresh_token: string }
  setTokens(body.access_token, body.refresh_token)
}

export async function sendLoginEmailOtp(email: string): Promise<void> {
  const r = await fetch(`${API_BASE}/auth/login/email-otp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(apiErrorMessage(body, `Could not send code (${r.status})`))
  }
}

export async function logout(): Promise<void> {
  if (!isNative) {
    await fetch(`${API_BASE}${ROUTES.logout}`, { method: 'POST', credentials: ROUTES.credentials }).catch(() => {})
    clearTokens()
    return
  }
  const rt = getRefreshToken()
  if (rt) {
    await fetch(`${API_BASE}${ROUTES.logout}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    }).catch(() => {})
  }
  clearTokens()
}

export interface MfaMethods {
  totp_enabled: boolean
  email_otp_enabled: boolean
  recovery_codes_remaining: number
}

export async function fetchMfaMethods(): Promise<MfaMethods> {
  const r = await apiFetch('/auth/2fa/methods')
  if (!r.ok) throw new Error(`/auth/2fa/methods ${r.status}`)
  return r.json() as Promise<MfaMethods>
}

/** Error carrying the HTTP status so the UI can tell wrong-code, rate-limit and outage apart. */
export class HttpError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

export async function sendStepupEmailOtp(): Promise<void> {
  const r = await apiFetch('/auth/2fa/email-otp/send', { method: 'POST' })
  if (!r.ok) throw new HttpError(r.status, `email-otp/send ${r.status}`)
}

/** Confirms the emailed code and switches email-code MFA on (also creates the recovery codes). */
export async function verifyEmailOtpEnrollment(code: string): Promise<void> {
  const r = await apiFetch('/auth/2fa/email-otp/verify', { method: 'POST', body: JSON.stringify({ code }) })
  if (!r.ok) throw new HttpError(r.status, `email-otp/verify ${r.status}`)
}

/** Issues a fresh set of one-time recovery codes (shown once, right after enrolment). */
export async function regenerateRecoveryCodes(): Promise<string[]> {
  const r = await apiFetch('/auth/2fa/recovery-codes/regenerate', { method: 'POST' })
  if (!r.ok) throw new HttpError(r.status, `recovery-codes/regenerate ${r.status}`)
  return ((await r.json()) as { codes: string[] }).codes
}

export async function verifyStepup(
  app: StepupApp,
  method: 'totp' | 'email_otp' | 'recovery_code',
  code: string,
): Promise<void> {
  const r = await apiFetch('/auth/stepup/verify', {
    method: 'POST',
    headers: { 'X-Client': 'native' },
    body: JSON.stringify({ app, method, code }),
  })
  if (!r.ok) throw new HttpError(r.status, 'step-up verification failed')
  const body = await r.json() as { app?: StepupApp; token?: string }
  if (!body.token) throw new Error('step-up token missing (X-Client header not honoured)')
  setStepupToken(app, body.token)
}

export async function register(data: {
  email: string
  email_confirm: string
  name: string
  password: string
  password_confirm: string
  tos_accepted: boolean
}): Promise<void> {
  const r = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(apiErrorMessage(body, `Registration failed (${r.status})`))
  }
}

export async function forgotPasswordRequest(email: string): Promise<void> {
  const r = await fetch(`${API_BASE}/auth/me/password/reset-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!r.ok && r.status !== 204) {
    const body = await r.json().catch(() => ({}))
    throw new Error(apiErrorMessage(body, `Request failed (${r.status})`))
  }
}

export async function forgotPasswordConfirm(data: {
  email: string
  code: string
  new_password: string
  new_password_confirm: string
}): Promise<void> {
  const r = await fetch(`${API_BASE}/auth/me/password/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(apiErrorMessage(body, `Reset failed (${r.status})`))
  }
}
